import { api } from './api.ts'
import Config from '../helpers/Config'
import { Site, SiteLogo } from '../types/site.ts'
import { logApiError } from '../helpers/TelemetryHelper'

// Define a service using a base URL and expected endpoints
export const siteApi = api.injectEndpoints({
    endpoints: (build) => ({
        getSiteInfo: build.query({
            query: () => {
                const params = {
                    requestParameters: {
                        Data: []
                    }
                }
                return {
                    url: Config.END_POINT_NAMES.SITE_CONFIG,
                    method: 'POST',
                    body: params,
                    providesTags: (result = []) => [
                        ...result.map(({ id }) => ({ type: 'SiteInfo', id } as const)),
                        { type: 'SiteInfo' as const, id: 'LIST' },
                    ],
                }
            },
            transformResponse: (response: any): Site => {
                if (!response?.data) {
                    console.error('site-config: response.data is missing or null', response)
                    logApiError('SITE_CONFIG_TRANSFORM_EMPTY', Config.END_POINT_NAMES.SITE_CONFIG, {
                        hasResponse: !!response,
                        responseKeys: response ? Object.keys(response) : [],
                        statusCode: response?.statusCode,
                        errorMessage: response?.errorMessage ?? response?.statusMessage,
                    })
                    throw new Error('site-config returned empty data')
                }
                const d = response.data
                const allLocations = Array.isArray(d.AllLocations) ? d.AllLocations : []
                return {
                    id: d.SiteID,
                    name: d.SiteName,
                    companyNumber: d.CompanyNumber ?? 0,
                    isChatEnabled: d.ChatEnabled,
                    isSubmitOrderEnabled: d.SubmitOrderEnabled,
                    selfRegister: d.SelfRegister,
                    loginSystem: d.LoginSystem,
                    allLocations: allLocations.map((location: any) => ({
                        address1: location.Address1,
                        address2: location.Address2,
                        city: location.City,
                        stateCode: location.State,
                        countryCode: 'US',
                        postalCode: location.Zip,
                        phone: location.Phone,
                        axLocationId: location.AXLocationAcountID,
                        locationId: location.LocationID
                    })),
                    defaultLocation: {
                        firstName: d.DefaultLocation?.FirstName ?? '',
                        lastName: d.DefaultLocation?.LastName ?? '',
                        fullName: (d.DefaultLocation?.FirstName ?? '') + ' ' + (d.DefaultLocation?.LastName ?? ''),
                        address1: d.DefaultLocation?.Address1 ?? '',
                        address2: d.DefaultLocation?.Address2 ?? '',
                        city: d.DefaultLocation?.City ?? '',
                        stateCode: d.DefaultLocation?.State ?? '',
                        countryCode: 'US',
                        postalCode: d.DefaultLocation?.Zip ?? '',
                        phone: d.DefaultLocation?.Phone ?? '',
                        axLocationId: d.DefaultLocation?.AXLocationAcountID ?? '',
                        locationId: d.DefaultLocation?.LocationID ?? '',
                        c_addressType: d.DefaultLocation?.c_addressType ?? '',
                        c_isSmartyValidated: d.DefaultLocation?.c_isSmartyValidated ?? false,
                        allotmentAddressLabel: '',
                        allotmentAddressId: 0,
                        isRestrictedAddress: false

                    },
                    pbeEnabled: d.PBEEnabled,
                    qualtricsEnabled: d.QualtricsEnabled,
                    displayPBERecipientListOnPDP: d.DisplayPBERecepientListOnPDP,
                    shipToLocationEnabled: d.ShipToLocationEnabled,
                    isPayrollDeductionEnabled: d.IsPayrollDeductionEnabled,
                    enableGiftCardWithEmployeeCredits: d.EnableGiftCardWithEmployeeCredits ?? false,
                    smartyACSettings: {
                        ...(d.SmartyACSettings ?? {}),
                    },
                    smartyAVSSettings: {
                        ...(d.SmartyAVSSettings ?? {}),
                    },
                    billingAddressEditable: d.BillingAddressEditable,
                    shippingAddressEditable: d.ShippingAddressEditable,
                    embChoiceDisplayDefault: d.EmbChoiceDisplayDefault,
                    embChoiceDisplayGreaterThan: d.EmbChoiceDisplayGreaterThan,
                    embChoiceDisplayGreaterThanOverride: d.EmbChoiceDisplayGreaterThanOverride,
                    embChoiceDropdownDefaultOptionText: d.EmbChoiceDropdownDefaultOptionText,
                    minSizeOptionsForDropdown: d.MinSizeOptionsForDropdown,
                    allotmentCompleteContinueShopping: d.AllotmentCompleteContinueShopping,
                    categories: d.Categories,
                    trackingLink: d.TrackingLink,
                    additionalSettings: d.AdditionalSettings || {},
                    enableSSO: d.EnableSSO,
                    samlLoginEndpoint: d.SAMLLoginEndpoint,
                    samlLogoutEndpoint: d.SAMLLogoutEndpoint,
                    sizingPortalEnabled: d.SizingPortalEnabled,
                    orderConfirmationMessage: d.OrderConfirmationMessage,
                    isShippingTaxExempt: d.IsShippingTaxExempt,
                    isTaxExemptAppliedToMaxItem: d.IsTaxExemptAppliedToMaxItem,
                    listIgnoreTaxExempt: d.ListIgnoreTaxExempt,
                    shipToLocationAddressFallback: d.ShipToLocationAddressFallback,
                    payrollDeductionSelectOptions: d.PayrollDeductionSelectOptions,
                    isPersonalInfoModificationAllowed: d.IsPersonalInfoModificationAllowed,
                    isDedicatedAllotmentAddressesEnabled: d.IsDedicatedAllotmentAddressesEnabled,
                    isAddAllotmentAddressDuringCheckoutEnabled: d.IsAddAllotmentAddressDuringCheckoutEnabled,
                    isPurchaseOrderNumberRequired: d.IsPurchaseOrderNumberRequired,
                    enablePayPalForOrderAmountGreaterThen: d.EnablePayPalForOrderAmountGreaterThen,
                    enablePayPalPayments: d.EnablePayPalPayments,
                    isPayPalRestrictBasketTotal: d.Is_PP_Restrict_Basket_Total,
                    paypalRestrictBasketTotalValue: d.PP_Restrict_Basket_Total_Value,
                    ppApiApplePayEnabled: d.PP_API_ApplePay_Enabled,
                    ppApiApplePaySdkUrl: d.PP_API_ApplePay_SDK_URL,
                    contentAssets: d.ContentAssets,
                    enableLogRocket: d.EnableLogRocket ?? false,
                    logRocketAppID: d.LogRocketAppID ?? '',
                    lrClientIpHash: d.LrClientIpHash ?? '',
                    blockDirectLogin: d.BlockDirectLogin === true,
                }
            }
        }),
        getSiteLogo: build.query({
            query: (payload) => {
                const params = {
                    requestParameters: {
                        recordValueJson: "[]"
                    }
                }
                return {
                    url: Config.END_POINT_NAMES.GET_WEBSITE_LOGO,
                    method: 'POST',
                    body: params,
                    providesTags: (result = []) => [
                        ...result.map(({ id }) => ({ type: 'SiteInfo', id } as const)),
                        { type: 'SiteInfo' as const, id: 'LIST' },
                    ],
                }
            },
            transformResponse: (response: any): SiteLogo => {
                return {
                    id: response.data?.[0].AppConfigKey,
                    url: response.data?.[0].AppConfigValue,
                } as SiteLogo
            }
        }),
        getLocaleData: build.query({
            query: (payload) => {
                const params = {
                    requestParameters: {
                        entityId: payload.entityId,
                        languageCode: payload.languageCode,
                        recordValueJson: "[]",
                    },
                };
                return {
                    url: Config.END_POINT_NAMES.GET_LOCALIZATION_CSTM_PORTAL,
                    method: 'POST',
                    body: params,
                    providesTags: (result = []) => [
                        ...result.map(({ id }) => ({ type: 'Locales', id } as const)),
                        { type: 'Locales' as const, id: 'LIST' },
                    ],
                }
            }
        }),
        getCategories: build.query({
            query: (payload) => {
                const params = {
                    requestParameters: {
                        PageNo: 1,
                        PageSize: 100,
                        recordValueJson: "[]",
                    },
                };
                return {
                    url: Config.END_POINT_NAMES.GET_CATEGORIES_LIST,
                    method: 'POST',
                    body: params,
                    providesTags: ['Categories']
                }
            },
            transformResponse: (response: any) => {
                return response.data
            }
        }),
        getSmartySuggestions: build.query({
            query: (payload) => {
                const params = {
                    requestParameters: {
                        Street: payload.search,
                        Selected: '',
                        Max: 10
                    },
                };
                return {
                    url: Config.END_POINT_NAMES.SUGGEST_ADDRESS,
                    method: 'POST',
                    body: params,
                    providesTags: (result = [], error: any) => {
                        if (!result || error) return [{ type: 'SmartySuggestions', id: 'LIST' }]
                        return [
                            ...result.map(({ zip }) => ({ type: 'SmartySuggestions', id: zip } as const)),
                            { type: 'SmartySuggestions', id: 'LIST' },
                        ]
                    }
                }
            }
        }),
        postContactUs: build.mutation({
            query: (payload) => {
                const params = {
                    requestParameters: {
                        ...payload
                    },
                };
                return {
                    url: Config.END_POINT_NAMES.CONTACT_US,
                    method: 'POST',
                    body: params,
                }
            },
        }),
    })
})

export const {
    useGetSiteInfoQuery,
    useLazyGetSiteInfoQuery,
    useGetSiteLogoQuery,
    useGetLocaleDataQuery,
    useGetCategoriesQuery,
    useGetSmartySuggestionsQuery,
    useLazyGetSmartySuggestionsQuery,
    usePostContactUsMutation
} = siteApi
